<template>
    <div class="h-100">
        <b-row align-h="center">
            <b-col cols="12" md="10">
                <b-link :to="{ name: 'settings' }">
                    <font-awesome-icon icon="arrow-left" />
                    {{ $t("Back") }}
                </b-link>
                <h1 class="mt-1 mb-2">{{ $t("settings.title") }} / {{ $t("settings.smart-routing.title") }}</h1>

                <div v-if="!isLoading" class="mb-2">
                    <b-row align-h="between">
                        <b-col class="d-flex" cols="12" md="6">
                            <b-button
                                id="contacts-add"
                                :to="{ name: 'settings-smartrouting-add' }"
                                class="mr-2 text-nowrap"
                                variant="primary"
                            >
                                <font-awesome-icon class="mr-50" icon="plus" />
                                <span>{{ $t("settings.smart-routing.add-route") }}</span>
                            </b-button>

                            <div class="d-flex align-items-center justify-content-end">
                                <b-form-input
                                    v-model="searchQuery"
                                    :placeholder="$t('paginator.search')"
                                    class="d-inline-block mr-1 bg-light"
                                    debounce="500"
                                />
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <template v-if="isLoading">
                    <div class="d-flex justify-content-center my-1">
                        <b-spinner />
                    </div>
                </template>
                <b-table-simple v-else>
                    <b-thead>
                        <b-tr>
                            <b-th>#</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.name") }}</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.channel") }}</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.assignee") }}</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.tags") }}</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.to-match") }}</b-th>
                            <b-th>{{ $t("settings.smart-routing.list.actions") }}</b-th>
                        </b-tr>
                    </b-thead>
                    <draggable
                        v-model="routes"
                        :options="{ disabled: searchQuery }"
                        tag="tbody"
                        @change="rowsReordered"
                    >
                        <b-tr v-for="route in filteredRoutes" :id="`route-${route.id}`" :key="`route-${route.id}`">
                            <b-td>{{ route.index }}</b-td>
                            <b-td>
                                <strong>{{ route.name }}</strong>
                            </b-td>
                            <b-td>
                                <font-awesome-icon
                                    :id="`channel-${route.channel.id}-route-${route.id}-icon`"
                                    :key="`channel-${route.channel.id}-route-${route.id}-icon`"
                                    :icon="resolvePlatformIcon(route.channel.provider, route.channel.platform)"
                                    class="route-channel-icon"
                                />
                                {{ route.channel.name }}
                            </b-td>
                            <b-td>
                                <b-badge
                                    v-if="getAssignee(route)"
                                    :style="styleByColor(route.team.color)"
                                    variant="text-secondary"
                                >
                                    <font-awesome-icon
                                        v-if="route.team && route.team.name && !route.user"
                                        :icon="['fas', 'users']"
                                    />
                                    {{ getAssignee(route) }}
                                </b-badge>
                            </b-td>
                            <b-td>
                                <b-badge v-if="route.tags && route.tags[0]" variant="light text-secondary"
                                    >{{ route.tags[0].name }}
                                </b-badge>
                                <b-badge
                                    v-if="route.tags && route.tags.length > 1"
                                    v-b-tooltip.hover.top="
                                        route.tags
                                            .slice(1)
                                            .map((tag) => tag.name)
                                            .join(', ')
                                    "
                                    class="ml-1"
                                    variant="light text-secondary"
                                    >+{{ route.tags.length - 1 }}
                                </b-badge>
                            </b-td>
                            <b-td>
                                <b>{{ $t("settings.routes.words") }}: {{ route.keywords.length }}</b
                                ><br />
                                {{ route.keywords.map((keyword) => keyword.keyword).join(", ") }}
                            </b-td>
                            <b-td class="text-right">
                                <b-button
                                    :to="{
                                        name: 'settings-smartrouting-edit',
                                        params: { id: route.id },
                                    }"
                                    class="btn-icon mr-50"
                                    variant="light text-secondary"
                                >
                                    <font-awesome-icon icon="pen" />
                                </b-button>
                                <b-button
                                    class="btn-icon mr-50"
                                    variant="light text-secondary"
                                    @click="deleteRoute(route.id)"
                                >
                                    <font-awesome-icon icon="trash" />
                                </b-button>
                            </b-td>
                        </b-tr>
                    </draggable>
                </b-table-simple>
            </b-col>
        </b-row>
    </div>
</template>
<script>
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axiosV1 from "@/libs/axiosV1";
import i18n from "@/libs/i18n";
import { resolvePlatformIcon } from "@/utils/channels";
import { computed, ref } from "@vue/composition-api";
import { styleByColor } from "@/utils/color";

import {
    BBadge,
    BButton,
    BCol,
    BContainer,
    BFormInput,
    BLink,
    BRow,
    BSpinner,
    BTableSimple,
    BTbody,
    BTd,
    BTh,
    BThead,
    BTr,
    VBTooltip,
} from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import draggable from "vuedraggable";
import useRoutes from "./useRoutes";

export default {
    components: {
        BTableSimple,
        BContainer,
        BRow,
        BCol,
        BThead,
        BTh,
        BTr,
        BTd,
        BTbody,
        BBadge,
        BButton,
        BFormInput,
        BLink,
        BSpinner,
        draggable,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    setup() {
        const toast = useToast();
        const { routes, fetchRoutes, isLoading } = useRoutes();
        const searchQuery = ref("");
        const filteredRoutes = computed(() => {
            return searchQuery.value
                ? [
                      ...routes.value.filter((route) =>
                          route.name.toLowerCase().includes(searchQuery.value.toLowerCase())
                      ),
                  ]
                : [...routes.value];
        });

        fetchRoutes();

        return {
            routes,
            filteredRoutes,
            resolvePlatformIcon,
            fetchRoutes,
            styleByColor,
            searchQuery,
            isLoading,
            toast,
        };
    },
    methods: {
        rowsReordered(event) {
            axiosV1
                .patch(`routes/indices/old-index/${event.moved.oldIndex}/new-index/${event.moved.newIndex}`)
                .then(() => {
                    this.routes.forEach((route, index) => {
                        route.index = index;
                    });
                    this.toast.success(this.$t("settings.smart-routing.reordering.success"));
                })
                .catch(() => {
                    this.toast.success(this.$t("settings.smart-routing.reordering.error"));
                });
        },
        getAssignee(route) {
            if (route.user?.name) {
                return route.user.name;
            }
            if (route.team?.name) {
                return route.team.name;
            }
            return "";
        },
        deleteRoute(id) {
            this.$bvModal
                .msgBoxConfirm(this.$t("confirm.Are you sure?"), {
                    title: this.$t("confirm.Are you sure?"),
                    cancelTitle: this.$t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                    bodyClass: "mx-auto",
                    headerClass: "text-center",
                })
                .then((result) => {
                    if (result) {
                        axiosV1
                            .delete(`routes/${id}`)
                            .then(() => {
                                this.fetchRoutes();
                                this.toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("settings.smart-routing.smart-routing"),
                                        text: i18n.t("settings.smart-routing.deleting.success"),
                                        icon: "check",
                                        variant: "success",
                                    },
                                });
                            })
                            .catch(() => {
                                this.toast({
                                    component: ToastificationContent,
                                    props: {
                                        title: i18n.t("settings.smart-routing.smart-routing"),
                                        text: i18n.t("settings.smart-routing.deleting.error"),
                                        icon: "times",
                                        variant: "error",
                                    },
                                });
                            });
                    }
                });
        },
    },
};
</script>
